import React from "react";
import { Linking, StyleSheet, TouchableOpacity, View } from "react-native";
import { format, parseISO } from "date-fns";
import { useNavigation } from "@react-navigation/native";

import Text from "../Text";
import { VisitStatusColors, VisitStatusText } from "../../constants";
import { colors, fonts } from "../../theme";
import { Visit, VisitStatus } from "../../types";
import { isPastVisit } from "../../utils/visit";

type Props = {
  visit: Visit;
  featured?: boolean;
};

const serviceNames = (visit: Visit) => {
  const names = (visit.services || [])
    .map((item) => item?.service?.name)
    .filter(Boolean);
  if (!names.length) return "Service details pending";
  return names.length > 2
    ? `${names.slice(0, 2).join(", ")} +${names.length - 2}`
    : names.join(", ");
};

const artistNames = (visit: Visit) => {
  const names = Array.from(
    new Set(
      (visit.services || [])
        .map((item) => item?.assignedEmployee?.person?.name)
        .filter(Boolean),
    ),
  );
  return names.length ? names.join(", ") : "Artist not assigned";
};

const consentStep = (status: VisitStatus) => {
  if (status === VisitStatus.Draft) return 1;
  if (status === VisitStatus.ConsentRequested) return 3;
  return 4;
};

const StatusBadge = ({ status }: { status: VisitStatus }) => {
  const tint = VisitStatusColors[status];
  return (
    <View style={[styles.badge, { borderColor: tint }]}>
      <View style={[styles.dot, { backgroundColor: tint }]} />
      <Text variant="bold" style={[styles.badgeText, { color: tint }]}>
        {VisitStatusText[status]}
      </Text>
    </View>
  );
};

const VisitCard: React.FC<Props> = ({ visit, featured = false }) => {
  const navigation = useNavigation();
  const status = visit.status as VisitStatus;
  const date = visit.scheduledFor ? parseISO(visit.scheduledFor) : null;
  const requiresConsent =
    status === VisitStatus.Draft || status === VisitStatus.ConsentRequested;
  const openDetails = () => navigation.navigate("Visit", { visitId: visit.id });
  const continueConsent = () =>
    navigation.navigate("ExistingVisitFlow", { visitId: visit.id });

  if (!featured) {
    return (
      <TouchableOpacity onPress={openDetails} style={styles.compactCard}>
        <View style={styles.dateBlock}>
          <Text style={styles.month}>{date ? format(date, "MMM") : "TBD"}</Text>
          <Text variant="bold" style={styles.day}>
            {date ? format(date, "d") : "--"}
          </Text>
        </View>
        <View style={styles.compactBody}>
          <View style={styles.compactTop}>
            <Text variant="bold" numberOfLines={1} style={styles.storeName}>
              {visit.store.name}
            </Text>
            <StatusBadge status={status} />
          </View>
          <Text numberOfLines={1} style={styles.serviceText}>
            {serviceNames(visit)}
          </Text>
          <Text numberOfLines={1} style={styles.metaText}>
            {date ? format(date, "h:mm a") : "Date not scheduled"} |{" "}
            {artistNames(visit)}
          </Text>
        </View>
        <Text style={styles.chevron}>{">"}</Text>
      </TouchableOpacity>
    );
  }

  const activeStep = consentStep(status);
  return (
    <View style={styles.featuredCard}>
      <TouchableOpacity onPress={openDetails}>
        <View style={styles.featuredTop}>
          <Text style={styles.eyebrow}>
            {date ? "NEXT VISIT" : "VISIT REQUEST"}
          </Text>
          <StatusBadge status={status} />
        </View>
        <Text variant="bold" style={styles.featuredDate}>
          {date ? format(date, "EEEE, MMMM d") : "Date to be scheduled"}
        </Text>
        <Text style={styles.featuredTime}>
          {date ? format(date, "h:mm a") : "Complete your paperwork when ready"}
        </Text>
        <View style={styles.rule} />
        <Text variant="bold" style={styles.featuredStore}>
          {visit.store.name}
        </Text>
        <Text style={styles.featuredService}>{serviceNames(visit)}</Text>
        <Text style={styles.featuredArtist}>{artistNames(visit)}</Text>
      </TouchableOpacity>

      {requiresConsent && (
        <View style={styles.progressSection}>
          <View style={styles.progressHeader}>
            <Text variant="bold" style={styles.progressTitle}>
              Your paperwork
            </Text>
            <Text style={styles.progressCount}>{activeStep} of 4</Text>
          </View>
          <View style={styles.progressTrack}>
            {[1, 2, 3, 4].map((step) => (
              <View
                key={step}
                style={[
                  styles.progressSegment,
                  step <= activeStep && styles.progressSegmentActive,
                ]}
              />
            ))}
          </View>
          <View style={styles.progressLabels}>
            <Text style={styles.progressLabel}>Details</Text>
            <Text style={styles.progressLabel}>Services</Text>
            <Text style={styles.progressLabel}>Consent</Text>
            <Text style={styles.progressLabel}>Sign</Text>
          </View>
          <TouchableOpacity
            onPress={continueConsent}
            style={styles.continueButton}
          >
            <Text variant="bold" style={styles.continueText}>
              {status === VisitStatus.Draft
                ? "Begin paperwork"
                : "Continue paperwork"}
            </Text>
          </TouchableOpacity>
        </View>
      )}

      <View style={styles.actions}>
        <TouchableOpacity
          onPress={openDetails}
          style={[styles.action, styles.primaryAction]}
        >
          <Text variant="bold" style={styles.primaryActionText}>
            View details
          </Text>
        </TouchableOpacity>
        {!!visit.store?.phoneNumber && (
          <TouchableOpacity
            onPress={() => Linking.openURL(`tel:${visit.store.phoneNumber}`)}
            style={styles.action}
          >
            <Text variant="bold" style={styles.actionText}>
              Call shop
            </Text>
          </TouchableOpacity>
        )}
      </View>
    </View>
  );
};

const styles = StyleSheet.create({
  compactCard: {
    alignItems: "center",
    backgroundColor: colors.white,
    borderRadius: 12,
    flexDirection: "row",
    marginBottom: 10,
    minHeight: 84,
    padding: 12,
  },
  dateBlock: {
    alignItems: "center",
    borderRightColor: colors.lightGrey,
    borderRightWidth: 1,
    paddingRight: 13,
    width: 58,
  },
  month: { color: colors.midGrey, fontSize: 10, textTransform: "uppercase" },
  day: { color: colors.black, fontSize: 23, lineHeight: 27 },
  compactBody: { flex: 1, paddingLeft: 13 },
  compactTop: { alignItems: "center", flexDirection: "row" },
  storeName: { color: colors.black, flex: 1, fontSize: 14, marginRight: 8 },
  serviceText: { color: colors.black, fontSize: 13, marginTop: 4 },
  metaText: { color: colors.midGrey, fontSize: 11, marginTop: 2 },
  chevron: { color: colors.midGrey, fontSize: 20, marginLeft: 8 },
  badge: {
    alignItems: "center",
    borderRadius: 12,
    borderWidth: 1,
    flexDirection: "row",
    paddingHorizontal: 7,
    paddingVertical: 3,
  },
  dot: { borderRadius: 4, height: 5, marginRight: 5, width: 5 },
  badgeText: {
    fontFamily: fonts.semiBold,
    fontSize: 9,
    letterSpacing: 0.2,
    textTransform: "uppercase",
  },
  featuredCard: {
    backgroundColor: colors.white,
    borderRadius: 18,
    marginBottom: 14,
    overflow: "hidden",
    padding: 24,
  },
  featuredTop: {
    alignItems: "center",
    flexDirection: "row",
    justifyContent: "space-between",
  },
  eyebrow: { color: colors.midGrey, fontSize: 10, letterSpacing: 1.4 },
  featuredDate: { color: colors.black, fontSize: 25, marginTop: 14 },
  featuredTime: { color: colors.midGrey, fontSize: 16, marginTop: 1 },
  rule: { backgroundColor: colors.lightGrey, height: 1, marginVertical: 16 },
  featuredStore: { color: colors.black, fontSize: 18 },
  featuredService: { color: colors.black, fontSize: 14, marginTop: 7 },
  featuredArtist: { color: colors.midGrey, fontSize: 13, marginTop: 3 },
  progressSection: {
    backgroundColor: colors.offWhite,
    borderRadius: 12,
    marginTop: 20,
    padding: 15,
  },
  progressHeader: { flexDirection: "row", justifyContent: "space-between" },
  progressTitle: { color: colors.black, fontSize: 13 },
  progressCount: { color: colors.midGrey, fontSize: 11 },
  progressTrack: { flexDirection: "row", marginHorizontal: -2, marginTop: 11 },
  progressSegment: {
    backgroundColor: colors.lightGrey,
    borderRadius: 3,
    flex: 1,
    height: 5,
    marginHorizontal: 2,
  },
  progressSegmentActive: { backgroundColor: colors.yellow },
  progressLabels: {
    flexDirection: "row",
    justifyContent: "space-between",
    marginTop: 6,
  },
  progressLabel: { color: colors.midGrey, fontSize: 9 },
  continueButton: {
    alignItems: "center",
    backgroundColor: colors.yellow,
    borderRadius: 22,
    marginTop: 13,
    paddingVertical: 11,
  },
  continueText: {
    color: colors.black,
    fontSize: 12,
    textTransform: "uppercase",
  },
  actions: {
    borderTopColor: colors.lightGrey,
    borderTopWidth: 1,
    flexDirection: "row",
    marginHorizontal: -4,
    marginTop: 18,
    paddingTop: 14,
  },
  action: {
    alignItems: "center",
    backgroundColor: colors.offWhite,
    borderRadius: 9,
    flex: 1,
    justifyContent: "center",
    marginHorizontal: 4,
    paddingHorizontal: 10,
    paddingVertical: 10,
  },
  primaryAction: {
    backgroundColor: colors.yellow,
  },
  actionText: {
    color: colors.black,
    fontSize: 10,
    textAlign: "center",
    textTransform: "uppercase",
  },
  primaryActionText: {
    color: colors.black,
    fontFamily: fonts.semiBold,
    fontSize: 10,
    textAlign: "center",
    textTransform: "uppercase",
  },
});

export default VisitCard;
